/**
 * Reads in environment variables here
 *
 * Since our app isn't ready to move everything to environment variables,
 * we need to compute some of the client and API base URLs for different domains
 * e.g. localhost:####, app.dev.metrop.io, app.metropolis.io
 *
 * Next phase, we should retrieve all client and site API urls from environment variables and remove the reliance of the utility functions
 */

import { AppName } from '@m/types/AppName';
import { z } from 'zod';

// Environment variable schema
const envSchema = z.object({
  // Configuration
  REACT_APP_CONFIG_ENV: z.enum(['local', 'development', 'staging', 'production', 'qa', 'qa-fe']),
  REACT_APP_NAME: z.string().optional(),
  REACT_APP_VERSION: z.string().optional(),

  // API URLs - Required
  REACT_APP_SITE_API_BASE_URL: z.url(),
  REACT_APP_EDGE_API_GRPC_URL: z.url(),
  REACT_APP_HOSPITALITY_API_GRPC_URL: z.url(),
  REACT_APP_COVERAGE_PRODUCT_API_GRPC_URL: z.url(),
  REACT_APP_DEVICE_MANAGER_API_GRPC_URL: z.url(),
  REACT_APP_ENFORCEMENT_API_GRPC_URL: z.url(),
  REACT_APP_RULES_ENGINE_API_GRPC_URL: z.url(),
  REACT_APP_MARKET_API_GRPC_URL: z.url(),
  REACT_APP_MEMBER_API_GRPC_URL: z.url(),
  REACT_APP_OOSTO_API_GRPC_URL: z.url(),
  REACT_APP_PAYMENT_API_GRPC_URL: z.url(),
  REACT_APP_PRICING_API_BASE_URL: z.url(),
  REACT_APP_AI_PRICING_API_GRPC_URL: z.url(),
  REACT_APP_SITE_CONFIG_API_BASE_URL: z.url(),
  REACT_APP_PROSPECTING_API_GRPC_URL: z.url(),
  REACT_APP_VISIT_API_GRPC_URL: z.url(),
  REACT_APP_RESERVATION_API_BASE_URL: z.url(),

  // Edge Hardware UI
  REACT_APP_EDGE_HARDWARE_UI_URL: z.url(),

  // Keycloak - Required
  REACT_APP_KEYCLOAK_URL: z.url(),
  REACT_APP_KEYCLOAK_REALM: z.string(),
  REACT_APP_KEYCLOAK_CLIENT: z.string(),

  // Client Application URLs - Required
  REACT_APP_ADMIN_APP_URL: z.url(),
  REACT_APP_CUSTOMER_APP_URL: z.url(),
  REACT_APP_DISPLAYS_APP_URL: z.url(),
  REACT_APP_SPECIALIST_APP_URL: z.url(),
  REACT_APP_PAY_APP_URL: z.url(),
  REACT_APP_PORTAL_APP_URL: z.url(),
  REACT_APP_INTAKE_APP_URL: z.url(),
  REACT_APP_VALIDATION_APP_URL: z.url(),
  REACT_APP_REQUEST_APP_URL: z.url(),

  // Third-party Services - Required
  REACT_APP_MAPBOX_ACCESS_TOKEN: z.string(),

  // Analytics & Monitoring - Optional
  REACT_APP_HEAP_APP_ID: z.string().optional(),
  REACT_APP_GA4_MEASUREMENT_ID: z.string().optional(),
  REACT_APP_GOOGLE_ADS_ID: z.string().optional(),
  // Bare conversion label only (e.g. `1FreCITcze4ZEM_9m_A9`) — the `AW-` account id
  // lives in REACT_APP_GOOGLE_ADS_ID and the `send_to` value is composed in code.
  REACT_APP_GOOGLE_ADS_RESERVATION_LABEL: z.string().optional(),
  REACT_APP_DD_APP_ID: z.string().optional(),
  REACT_APP_DD_CLIENT_TOKEN: z.string().optional(),
  REACT_APP_ITERABLE_WEB_API_KEY: z.string().optional(),
  REACT_APP_TERMLY_WEBSITE_UUID: z.string().optional(),
  REACT_APP_ITERABLE_PACKAGE_NAME: z.string(),
  REACT_APP_ITERABLE_DASHBOARD_HERO_PLACEMENT_ID: z.coerce.number().int().optional(),
  REACT_APP_ITERABLE_RECEIPT_HERO_PLACEMENT_ID: z.coerce.number().int().optional(),
  REACT_APP_ITERABLE_OPEN_VISIT_PLACEMENT_ID: z.coerce.number().int().optional(),
  REACT_APP_HCAPTCHA_SITE_KEY: z.string().optional(),

  // External API (token auth) - Optional, default false
  REACT_APP_CONFIG_USE_EXTERNAL_API: z
    .string()
    .optional()
    .default('false')
    // REACT_APP_CONFIG_USE_EXTERNAL_API is set to the API env variable so if its present, it will be true
    .transform((s) => !!s && !(s === 'false' || s === '0')),
});

// Validate and parse environment variables
const parseEnv = () => {
  // Support both REACT_APP_ and NEXT_PUBLIC_ prefixes
  // TODO: Remove REACT_APP_ and refactor when migrating to Next.js
  const parsed = envSchema.safeParse({
    REACT_APP_ADMIN_APP_URL:
      process.env.REACT_APP_ADMIN_APP_URL || process.env.NEXT_PUBLIC_ADMIN_APP_URL,
    REACT_APP_CONFIG_ENV: process.env.REACT_APP_CONFIG_ENV || process.env.NEXT_PUBLIC_CONFIG_ENV,
    REACT_APP_CUSTOMER_APP_URL:
      process.env.REACT_APP_CUSTOMER_APP_URL || process.env.NEXT_PUBLIC_CUSTOMER_APP_URL,
    REACT_APP_DISPLAYS_APP_URL:
      process.env.REACT_APP_DISPLAYS_APP_URL || process.env.NEXT_PUBLIC_DISPLAYS_APP_URL,
    REACT_APP_DD_APP_ID: process.env.REACT_APP_DD_APP_ID || process.env.NEXT_PUBLIC_DD_APP_ID,
    REACT_APP_DD_CLIENT_TOKEN:
      process.env.REACT_APP_DD_CLIENT_TOKEN || process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
    REACT_APP_ITERABLE_WEB_API_KEY:
      process.env.REACT_APP_ITERABLE_WEB_API_KEY || process.env.NEXT_PUBLIC_ITERABLE_WEB_API_KEY,
    REACT_APP_TERMLY_WEBSITE_UUID:
      process.env.REACT_APP_TERMLY_WEBSITE_UUID || process.env.NEXT_PUBLIC_TERMLY_WEBSITE_UUID,
    REACT_APP_ITERABLE_PACKAGE_NAME:
      process.env.REACT_APP_ITERABLE_PACKAGE_NAME || process.env.NEXT_PUBLIC_ITERABLE_PACKAGE_NAME,
    REACT_APP_ITERABLE_DASHBOARD_HERO_PLACEMENT_ID:
      process.env.REACT_APP_ITERABLE_DASHBOARD_HERO_PLACEMENT_ID ||
      process.env.NEXT_PUBLIC_ITERABLE_DASHBOARD_HERO_PLACEMENT_ID,
    REACT_APP_ITERABLE_RECEIPT_HERO_PLACEMENT_ID:
      process.env.REACT_APP_ITERABLE_RECEIPT_HERO_PLACEMENT_ID ||
      process.env.NEXT_PUBLIC_ITERABLE_RECEIPT_HERO_PLACEMENT_ID,
    REACT_APP_ITERABLE_OPEN_VISIT_PLACEMENT_ID:
      process.env.REACT_APP_ITERABLE_OPEN_VISIT_PLACEMENT_ID ||
      process.env.NEXT_PUBLIC_ITERABLE_OPEN_VISIT_PLACEMENT_ID,
    REACT_APP_HEAP_APP_ID: process.env.REACT_APP_HEAP_APP_ID || process.env.NEXT_PUBLIC_HEAP_APP_ID,
    REACT_APP_GA4_MEASUREMENT_ID:
      process.env.REACT_APP_GA4_MEASUREMENT_ID || process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID,
    REACT_APP_GOOGLE_ADS_ID:
      process.env.REACT_APP_GOOGLE_ADS_ID || process.env.NEXT_PUBLIC_GOOGLE_ADS_ID,
    REACT_APP_GOOGLE_ADS_RESERVATION_LABEL:
      process.env.REACT_APP_GOOGLE_ADS_RESERVATION_LABEL ||
      process.env.NEXT_PUBLIC_GOOGLE_ADS_RESERVATION_LABEL,
    REACT_APP_HCAPTCHA_SITE_KEY:
      process.env.REACT_APP_HCAPTCHA_SITE_KEY || process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY,
    REACT_APP_INTAKE_APP_URL:
      process.env.REACT_APP_INTAKE_APP_URL || process.env.NEXT_PUBLIC_INTAKE_APP_URL,
    REACT_APP_KEYCLOAK_CLIENT:
      process.env.REACT_APP_KEYCLOAK_CLIENT || process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT,
    REACT_APP_KEYCLOAK_REALM:
      process.env.REACT_APP_KEYCLOAK_REALM || process.env.NEXT_PUBLIC_KEYCLOAK_REALM,
    REACT_APP_KEYCLOAK_URL:
      process.env.REACT_APP_KEYCLOAK_URL || process.env.NEXT_PUBLIC_KEYCLOAK_URL,
    REACT_APP_MAPBOX_ACCESS_TOKEN:
      process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
    REACT_APP_MARKET_API_GRPC_URL:
      process.env.REACT_APP_MARKET_API_GRPC_URL || process.env.NEXT_PUBLIC_MARKET_API_GRPC_URL,
    REACT_APP_MEMBER_API_GRPC_URL:
      process.env.REACT_APP_MEMBER_API_GRPC_URL || process.env.NEXT_PUBLIC_MEMBER_API_GRPC_URL,
    REACT_APP_OOSTO_API_GRPC_URL:
      process.env.REACT_APP_OOSTO_API_GRPC_URL || process.env.NEXT_PUBLIC_OOSTO_API_GRPC_URL,
    REACT_APP_EDGE_API_GRPC_URL:
      process.env.REACT_APP_EDGE_API_GRPC_URL || process.env.NEXT_PUBLIC_EDGE_API_GRPC_URL,
    REACT_APP_PAYMENT_API_GRPC_URL:
      process.env.REACT_APP_PAYMENT_API_GRPC_URL || process.env.NEXT_PUBLIC_PAYMENT_API_GRPC_URL,
    REACT_APP_PROSPECTING_API_GRPC_URL:
      process.env.REACT_APP_PROSPECTING_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_PROSPECTING_API_GRPC_URL,
    REACT_APP_HOSPITALITY_API_GRPC_URL:
      process.env.REACT_APP_HOSPITALITY_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_HOSPITALITY_API_GRPC_URL,
    REACT_APP_COVERAGE_PRODUCT_API_GRPC_URL:
      process.env.REACT_APP_COVERAGE_PRODUCT_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_COVERAGE_PRODUCT_API_GRPC_URL,
    REACT_APP_DEVICE_MANAGER_API_GRPC_URL:
      process.env.REACT_APP_DEVICE_MANAGER_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_DEVICE_MANAGER_API_GRPC_URL,
    REACT_APP_ENFORCEMENT_API_GRPC_URL:
      process.env.REACT_APP_ENFORCEMENT_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_ENFORCEMENT_API_GRPC_URL,
    REACT_APP_RULES_ENGINE_API_GRPC_URL:
      process.env.REACT_APP_RULES_ENGINE_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_RULES_ENGINE_API_GRPC_URL,
    REACT_APP_VISIT_API_GRPC_URL:
      process.env.REACT_APP_VISIT_API_GRPC_URL || process.env.NEXT_PUBLIC_VISIT_API_GRPC_URL,
    REACT_APP_EDGE_HARDWARE_UI_URL:
      process.env.REACT_APP_EDGE_HARDWARE_UI_URL || process.env.NEXT_PUBLIC_EDGE_HARDWARE_UI_URL,
    REACT_APP_NAME: process.env.REACT_APP_NAME || process.env.NEXT_PUBLIC_NAME,
    REACT_APP_PAY_APP_URL: process.env.REACT_APP_PAY_APP_URL || process.env.NEXT_PUBLIC_PAY_APP_URL,
    REACT_APP_PORTAL_APP_URL:
      process.env.REACT_APP_PORTAL_APP_URL || process.env.NEXT_PUBLIC_PORTAL_APP_URL,
    REACT_APP_PRICING_API_BASE_URL:
      process.env.REACT_APP_PRICING_API_BASE_URL || process.env.NEXT_PUBLIC_PRICING_API_BASE_URL,
    REACT_APP_AI_PRICING_API_GRPC_URL:
      process.env.REACT_APP_AI_PRICING_API_GRPC_URL ||
      process.env.NEXT_PUBLIC_AI_PRICING_API_GRPC_URL,
    REACT_APP_SITE_CONFIG_API_BASE_URL:
      process.env.REACT_APP_SITE_CONFIG_API_BASE_URL ||
      process.env.NEXT_PUBLIC_SITE_CONFIG_API_BASE_URL,
    REACT_APP_REQUEST_APP_URL:
      process.env.REACT_APP_REQUEST_APP_URL || process.env.NEXT_PUBLIC_REQUEST_APP_URL,
    REACT_APP_RESERVATION_API_BASE_URL:
      process.env.REACT_APP_RESERVATION_API_BASE_URL ||
      process.env.NEXT_PUBLIC_RESERVATION_API_BASE_URL,
    REACT_APP_SITE_API_BASE_URL:
      process.env.REACT_APP_SITE_API_BASE_URL || process.env.NEXT_PUBLIC_SITE_API_BASE_URL,
    REACT_APP_SPECIALIST_APP_URL:
      process.env.REACT_APP_SPECIALIST_APP_URL || process.env.NEXT_PUBLIC_SPECIALIST_APP_URL,
    REACT_APP_VALIDATION_APP_URL:
      process.env.REACT_APP_VALIDATION_APP_URL || process.env.NEXT_PUBLIC_VALIDATION_APP_URL,
    REACT_APP_VERSION: process.env.REACT_APP_VERSION || process.env.NEXT_PUBLIC_VERSION,
    REACT_APP_CONFIG_USE_EXTERNAL_API:
      process.env.REACT_APP_CONFIG_USE_EXTERNAL_API ||
      process.env.NEXT_PUBLIC_CONFIG_USE_EXTERNAL_API,
  });

  if (!parsed.success) {
    // eslint-disable-next-line no-console
    console.error('❌ Invalid environment variables:', parsed.error.issues);
    throw new Error('Invalid environment variables');
  }

  return parsed.data;
};

// Parse environment variables once at module load
const env = parseEnv();

// The admin app used to be under the 'admin' subdomain but is now under the 'manager' subdomain
export type AppSubdomain = Omit<AppName, 'admin' | 'performance'> | 'app' | 'manager';

export function getAppBaseUrl(appSubdomain: AppSubdomain) {
  if (appSubdomain === 'manager') return ADMIN_APP_URL;
  if (appSubdomain === 'app' || appSubdomain === 'customer') return CUSTOMER_APP_URL;
  if (appSubdomain === 'specialist') return SPECIALIST_APP_URL;
  if (appSubdomain === 'pay') return PAY_APP_URL;
  if (appSubdomain === 'portal') return PORTAL_APP_URL;
  if (appSubdomain === 'intake') return INTAKE_APP_URL;
  if (appSubdomain === 'validation') return VALIDATION_APP_URL;
  if (appSubdomain === 'request') return REQUEST_APP_URL;
  if (appSubdomain === 'displays') return DISPLAYS_APP_URL;
  return null;
}

// Configuration
export const CONFIG_ENV = env.REACT_APP_CONFIG_ENV;
export const APP_NAME = env.REACT_APP_NAME;
export const APP_VERSION = env.REACT_APP_VERSION;

// Keycloak
export const KEYCLOAK_URL = env.REACT_APP_KEYCLOAK_URL;
export const KEYCLOAK_REALM = env.REACT_APP_KEYCLOAK_REALM;
export const KEYCLOAK_CLIENT = env.REACT_APP_KEYCLOAK_CLIENT;

// Client Applications
export const ADMIN_APP_URL = env.REACT_APP_ADMIN_APP_URL;
export const CUSTOMER_APP_URL = env.REACT_APP_CUSTOMER_APP_URL;
export const DISPLAYS_APP_URL = env.REACT_APP_DISPLAYS_APP_URL;
export const INTAKE_APP_URL = env.REACT_APP_INTAKE_APP_URL;
export const PAY_APP_URL = env.REACT_APP_PAY_APP_URL;
export const PORTAL_APP_URL = env.REACT_APP_PORTAL_APP_URL;
export const REQUEST_APP_URL = env.REACT_APP_REQUEST_APP_URL;
export const SPECIALIST_APP_URL = env.REACT_APP_SPECIALIST_APP_URL;
export const VALIDATION_APP_URL = env.REACT_APP_VALIDATION_APP_URL;

// Site API
export const SITE_API_BASE_URL = env.REACT_APP_SITE_API_BASE_URL;

export const RESERVATION_API_BASE_URL = env.REACT_APP_RESERVATION_API_BASE_URL;

export const PRICING_API_BASE_URL = env.REACT_APP_PRICING_API_BASE_URL;

export const SITE_CONFIG_API_BASE_URL = env.REACT_APP_SITE_CONFIG_API_BASE_URL;

// See Mapbox and github secrets for token details.
// Production uses a token only with access to metropolis.io and metrop.io.
// Staging and Dev use a dummy token to minimize costs.
// Localhost uses a token only with access to localhost.
export const MAPBOX_ACCESS_TOKEN = env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Envoy proxy urls
export const EDGE_API_GRPC_URL = env.REACT_APP_EDGE_API_GRPC_URL;
export const HOSPITALITY_API_GRPC_URL = env.REACT_APP_HOSPITALITY_API_GRPC_URL;
export const COVERAGE_PRODUCT_API_GRPC_URL = env.REACT_APP_COVERAGE_PRODUCT_API_GRPC_URL;
export const DEVICE_MANAGER_API_GRPC_URL = env.REACT_APP_DEVICE_MANAGER_API_GRPC_URL;
export const ENFORCEMENT_API_GRPC_URL = env.REACT_APP_ENFORCEMENT_API_GRPC_URL;
export const RULES_ENGINE_API_GRPC_URL = env.REACT_APP_RULES_ENGINE_API_GRPC_URL;
export const MARKET_API_GRPC_URL = env.REACT_APP_MARKET_API_GRPC_URL;
export const MEMBER_API_GRPC_URL = env.REACT_APP_MEMBER_API_GRPC_URL;
export const OOSTO_API_GRPC_URL = env.REACT_APP_OOSTO_API_GRPC_URL;
export const PAYMENT_API_GRPC_URL = env.REACT_APP_PAYMENT_API_GRPC_URL;
export const PROSPECTING_API_GRPC_URL = env.REACT_APP_PROSPECTING_API_GRPC_URL;
export const VISIT_API_GRPC_URL = env.REACT_APP_VISIT_API_GRPC_URL;
export const AI_PRICING_API_GRPC_URL = env.REACT_APP_AI_PRICING_API_GRPC_URL;

// Edge Hardware UI
export const EDGE_HARDWARE_UI_URL = env.REACT_APP_EDGE_HARDWARE_UI_URL;

// Analytics & Monitoring
export const HEAP_APP_ID = env.REACT_APP_HEAP_APP_ID;
export const GA4_MEASUREMENT_ID = env.REACT_APP_GA4_MEASUREMENT_ID;
export const GOOGLE_ADS_ID = env.REACT_APP_GOOGLE_ADS_ID;
export const GOOGLE_ADS_RESERVATION_LABEL = env.REACT_APP_GOOGLE_ADS_RESERVATION_LABEL;
export const DD_APP_ID = env.REACT_APP_DD_APP_ID;
export const DD_CLIENT_TOKEN = env.REACT_APP_DD_CLIENT_TOKEN;
export const ITERABLE_WEB_API_KEY = env.REACT_APP_ITERABLE_WEB_API_KEY;
export const TERMLY_WEBSITE_UUID = env.REACT_APP_TERMLY_WEBSITE_UUID;
export const ITERABLE_PACKAGE_NAME = env.REACT_APP_ITERABLE_PACKAGE_NAME;
export const ITERABLE_DASHBOARD_HERO_PLACEMENT_ID =
  env.REACT_APP_ITERABLE_DASHBOARD_HERO_PLACEMENT_ID;
export const ITERABLE_RECEIPT_HERO_PLACEMENT_ID = env.REACT_APP_ITERABLE_RECEIPT_HERO_PLACEMENT_ID;
export const ITERABLE_OPEN_VISIT_PLACEMENT_ID = env.REACT_APP_ITERABLE_OPEN_VISIT_PLACEMENT_ID;

// hCaptcha
export const HCAPTCHA_SITE_KEY = env.REACT_APP_HCAPTCHA_SITE_KEY;

// External API (token auth when true)
export const USE_EXTERNAL_API = env.REACT_APP_CONFIG_USE_EXTERNAL_API;
